/* ==========================================================================
   masonry.js
   Masonry kiểu Pinterest cho lưới sản phẩm + windowing cho danh mục dài.
   --------------------------------------------------------------------------
   Vì sao cần: CSS `column-count` có thể CẮT một card qua 2 cột trên iOS
   Safari (font rendering khác Chrome -> card rơi vào ranh giới cột khác),
   làm tiêu đề/giá rơi sang cột bên -> nhìn như "mất title".
   Cách này chia card vào các cột bằng flexbox do JS tạo, card luôn NGUYÊN VẸN.

   KHÔNG gây nhảy trang khi cuộn:
     - Tải thêm sản phẩm (infinite scroll): chỉ ĐO & ĐẶT các thẻ MỚI vào cột
       thấp nhất (xếp tăng dần). Thẻ cũ KHÔNG bị di chuyển.
     - Chỉ dựng lại toàn lưới khi thật cần: lần đầu, đổi kích thước, đổi số cột,
       hoặc cấu trúc lưới bị theme thay (nạp trang trước, lọc...).

   WINDOWING (chỉ bật khi lưới dài, mặc định > 100 card):
     - Card ở XA màn hình bị "cất" phần nội dung vào bộ nhớ tạm và giữ chỗ bằng
       min-height; card GẦN màn hình được hoàn trả. Nhờ vậy DOM nhỏ, cuộn mượt
       dù danh mục rất dài.
     - Thẻ <li ref="cards[]"> LUÔN còn trong DOM -> logic phân trang của theme
       (đọc card đầu/cuối) không bị phá.
     - Việc cất nội dung chạy theo ngân sách mỗi frame để không chặn cuộn.
   Nếu JS lỗi/không chạy: CSS multi-column cũ vẫn là fallback.
   ========================================================================== */
(() => {
  'use strict';

  const DESKTOP_MAX_COLS = 4;
  const MOBILE_COLS = 2;
  // Bề rộng tối thiểu của card để tính số cột desktop, khớp với
  // util-product-grid-card-size.liquid (medium = 250/260px).
  const DESKTOP_MIN_CARD = 250;
  const CARD_SIZE_MIN = { small: 180, medium: 250, large: 340, 'extra-large': 480 };
  const COL_CLASS = 'masonry-col';
  const READY_ATTR = 'data-masonry';
  const WINDOWED_CLASS = 'masonry-windowed';
  const GRID_SEL = '.product-grid, .resource-list--grid';
  const ZOOM_OUT_SEL = '.product-grid[product-grid-view="zoom-out"]';
  const IGNORE = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK', 'NOSCRIPT']);

  // Windowing: bật khi lưới có nhiều hơn ngưỡng này card.
  // (có thể ép bằng window.__MASONRY_WINDOW_MIN cho mục đích kiểm thử)
  const WINDOW_MIN_CARDS = Number(window.__MASONRY_WINDOW_MIN) || 100;
  // Vùng "giữ thật" quanh viewport (200% ~ 2 màn hình mỗi phía).
  const WINDOW_MARGIN = '200% 0px';

  /** Trạng thái mỗi lưới: chiều cao từng cột, số cột, số thẻ đã xếp. */
  const state = new WeakMap();
  /** Đặt true để buộc dựng lại toàn lưới ở lần chạy kế tiếp (vd: đổi kích thước). */
  let rebuildAll = false;
  let busy = false;
  let timer = null;
  // Trạng thái cuộn: hoãn việc nặng (windowing, dựng lại) trong lúc đang cuộn.
  let scrolling = false;
  let scrollEndTimer = null;
  let pendingDeferred = false;

  const isCol = (el) => !!el.classList && el.classList.contains(COL_CLASS);
  const isItem = (el) => el.nodeType === 1 && !IGNORE.has(el.tagName);
  const inCol = (el) => !!el.parentElement && isCol(el.parentElement);
  const measure = (el) => el.getBoundingClientRect().height;

  /**
   * Số cột thực tế theo bề rộng lưới, không hardcode 4 cột cho mọi màn >=750px.
   * - Mobile: tôn trọng chế độ 1 cột của theme (nút "1 cột" và setting card lớn).
   * - Desktop/tablet/điện thoại xoay ngang: tính theo bề rộng card tối thiểu
   *   của section, nhờ đó 896px ra 2 cột thay vì 4 cột 174px.
   */
  function columnCount(container) {
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    if (!isDesktop) {
      if (container.getAttribute('product-grid-view') === 'mobile-single') return 1;
      if (container.classList.contains('product-grid-mobile--large')) return 1;
      const styles = getComputedStyle(container);
      // .product-grid (collection/search) đặt --mobile-columns;
      // .resource-list--grid (trang chủ, recommendations) đặt --column-count-mobile.
      // Trước đây chỉ đọc --mobile-columns -> lưới trang chủ luôn bị ép 2 cột
      // kể cả khi section đặt "1 cột" trên mobile.
      const raw =
        styles.getPropertyValue('--mobile-columns') || styles.getPropertyValue('--column-count-mobile');
      const mobileCols = Number.parseInt(raw, 10);
      return Number.isFinite(mobileCols) && mobileCols > 0 ? Math.min(MOBILE_COLS, mobileCols) : MOBILE_COLS;
    }

    const styles = getComputedStyle(container);
    const gap = Number.parseFloat(styles.columnGap) || 0;
    const width = container.clientWidth || window.innerWidth;
    const sizeSetting = container.getAttribute('data-product-card-size');
    const minCard = CARD_SIZE_MIN[sizeSetting] || DESKTOP_MIN_CARD;
    const fitting = Math.floor((width + gap) / (minCard + gap));

    // Theme luôn hiển thị tối thiểu 2 cột với card large/extra-large
    // (xem container query trong snippets/product-grid.liquid). Nếu không
    // đồng bộ, JS sẽ dựng 1 cột trên tablet 750–900px.
    const minCols = sizeSetting === 'large' || sizeSetting === 'extra-large' ? 2 : 1;

    return Math.max(minCols, Math.min(DESKTOP_MAX_COLS, fitting));
  }

  /**
   * Đo chiều cao một loạt card. Nếu card đang bị `content-visibility: auto` bỏ vẽ
   * (card có thể trả về chiều cao ước lượng), tạm bật visible cho lô NHỎ để đo
   * chính xác — vừa giữ cân bằng cột, vừa "nhớ" kích thước thật cho lần sau.
   */
  function measureList(els) {
    const forceVisible = els.length <= 80;
    if (forceVisible) {
      for (const el of els) el.style.contentVisibility = 'visible';
    }
    const hs = els.map(measure);
    if (forceVisible) {
      for (const el of els) el.style.contentVisibility = '';
    }
    return hs;
  }

  const orderOf = (el) => {
    const v = Number(el.dataset.masonryIndex);
    return Number.isNaN(v) ? Infinity : v;
  };

  /* ------------------------------------------------------------------ *
   *  Masonry                                                           *
   * ------------------------------------------------------------------ */

  /**
   * Gom TẤT CẢ card của lưới, dù chúng đang là con trực tiếp của lưới
   * (card mới do theme chèn khi cuộn / phân trang) hay đã nằm trong cột.
   */
  function getItems(container) {
    const items = [];
    for (const child of container.children) {
      if (isCol(child)) {
        for (const el of child.children) if (isItem(el)) items.push(el);
      } else if (isItem(child)) {
        items.push(child);
      }
    }
    return items;
  }

  /**
   * Gán chỉ số thứ tự (data-masonry-index) cho các card mới, giữ đúng vị trí chèn:
   *   - chèn TRƯỚC các card đã biết (nạp trang trước) -> đánh số nhỏ hơn
   *   - chèn SAU các card đã biết (cuộn nạp trang sau)  -> đánh số lớn hơn
   */
  function ensureOrder(items) {
    const known = items.filter((el) => el.dataset.masonryIndex !== undefined);
    if (!known.length) {
      items.forEach((el, i) => {
        el.dataset.masonryIndex = String(i);
      });
      return;
    }
    const fresh = items.filter((el) => el.dataset.masonryIndex === undefined);
    if (!fresh.length) return;

    let min = Infinity;
    let max = -Infinity;
    for (const el of known) {
      const v = Number(el.dataset.masonryIndex);
      if (v < min) min = v;
      if (v > max) max = v;
    }

    const firstKnown = items.findIndex((el) => el.dataset.masonryIndex !== undefined);
    const leading = [];
    const trailing = [];
    items.forEach((el, i) => {
      if (el.dataset.masonryIndex !== undefined) return;
      (i < firstKnown ? leading : trailing).push(el);
    });

    leading.forEach((el, i) => {
      el.dataset.masonryIndex = String(min - leading.length + i);
    });
    trailing.forEach((el, i) => {
      el.dataset.masonryIndex = String(max + 1 + i);
    });
  }

  /** Gỡ cấu trúc cột, trả card về con trực tiếp của lưới, ĐÚNG thứ tự gốc. */
  function uncolumn(container) {
    const cols = [...container.children].filter(isCol);
    if (!cols.length) return false;
    const items = cols.flatMap((c) => [...c.children].filter(isItem));
    cols.forEach((c) => c.remove());
    items.sort((a, b) => orderOf(a) - orderOf(b));
    items.forEach((el) => container.appendChild(el));
    return true;
  }

  /** Dựng lại toàn bộ lưới (dùng khi cấu trúc đổi, đổi kích thước...). */
  function rebuild(container, ordered) {
    restoreAll(container); // trả nội dung đang bị windowing để đo cho đúng
    uncolumn(container);

    const n = Math.max(1, Math.min(columnCount(container), ordered.length));
    const cols = [];
    for (let i = 0; i < n; i++) {
      const c = document.createElement('div');
      c.className = COL_CLASS;
      cols.push(c);
      container.appendChild(c);
    }

    // Pass 1: dua het vao cot dau de DO chieu cao chuan (moi cot cung be rong)
    for (const el of ordered) cols[0].appendChild(el);
    const hs = measureList(ordered);

    // Pass 2: xep vao cot thap nhat (Pinterest)
    const heights = new Array(n).fill(0);
    for (let i = 0; i < ordered.length; i++) {
      let best = 0;
      for (let k = 1; k < n; k++) if (heights[k] < heights[best] - 0.5) best = k;
      cols[best].appendChild(ordered[i]);
      heights[best] += hs[i];
    }

    state.set(container, { heights, n, count: ordered.length });
    container.setAttribute(READY_ATTR, 'ready');
  }

  /**
   * Xếp TĂNG DẦN: chỉ thêm các thẻ mới vào cột thấp nhất. Không đụng thẻ cũ.
   * Đây là điểm mấu chốt để cuộn không bị giật/nhảy.
   */
  function appendNew(container, strays, cols, st) {
    for (const el of strays) cols[0].appendChild(el);
    const hs = measureList(strays);

    const heights = st.heights;
    strays.forEach((el, i) => {
      let best = 0;
      for (let k = 1; k < cols.length; k++) if (heights[k] < heights[best] - 0.5) best = k;
      cols[best].appendChild(el);
      heights[best] += hs[i];
    });

    st.count += strays.length;
    state.set(container, st);
  }

  function layout(container, force) {
    const items = getItems(container);
    if (!items.length) return;

    ensureOrder(items);
    const ordered = [...items].sort((a, b) => orderOf(a) - orderOf(b));

    const cols = [...container.children].filter(isCol);
    const st = state.get(container);

    if (force || !st || !container.hasAttribute(READY_ATTR) || cols.length === 0 || cols.length !== st.n) {
      rebuild(container, ordered);
      return;
    }

    const inCols = ordered.filter(inCol);
    const strays = ordered.filter((el) => !inCol(el));

    // Cấu trúc đổi (bị xoá/thêm thẻ hàng loạt, lọc...) -> dựng lại
    if (inCols.length !== st.count) {
      rebuild(container, ordered);
      return;
    }
    if (!strays.length) return; // không có gì mới -> không làm gì (tránh nhảy trang)

    // Chỉ xử lý tăng dần khi mọi thẻ mới đều nằm SAU thẻ cũ (tải thêm ở cuối).
    let maxIndex = -Infinity;
    for (const el of inCols) {
      const v = Number(el.dataset.masonryIndex);
      if (v > maxIndex) maxIndex = v;
    }
    const allAppend = strays.every((el) => Number(el.dataset.masonryIndex) > maxIndex);
    if (!allAppend) {
      rebuild(container, ordered);
      return;
    }

    appendNew(container, strays, cols, st);
  }

  /* ------------------------------------------------------------------ *
   *  Windowing (chỉ bật khi lưới dài)                                  *
   * ------------------------------------------------------------------ */

  /** container -> { io, root, observed, frags: Map<card, DocumentFragment> } */
  const windows = new WeakMap();

  function getWin(container) {
    let w = windows.get(container);
    if (!w) {
      w = {
        io: null,
        root: undefined,
        observed: new WeakSet(),
        visible: new WeakSet(),
        frags: new Map(),
      };
      windows.set(container, w);
    }
    return w;
  }

  /** Gốc cuộn đúng như theme: desktop >=990px là .page-wrapper, còn lại là viewport. */
  function windowRoot() {
    return window.matchMedia('(min-width: 990px)').matches
      ? document.querySelector('.page-wrapper')
      : null;
  }

  /** Cất nội dung 1 card (giữ chỗ bằng min-height). */
  function detachCard(el, w) {
    if (w.frags.has(el) || !el.isConnected) return;
    if (w.visible.has(el)) return; // vừa trở lại vùng nhìn -> không cất
    const h = Math.round(el.getBoundingClientRect().height);
    if (h < 1) return;
    const frag = document.createDocumentFragment();
    while (el.firstChild) frag.appendChild(el.firstChild);
    el.style.minHeight = h + 'px';
    el.classList.add(WINDOWED_CLASS);
    w.frags.set(el, frag);
  }

  /** Hoàn trả nội dung 1 card. */
  function attachCard(el, w) {
    const frag = w.frags.get(el);
    if (!frag) return;
    el.classList.remove(WINDOWED_CLASS);
    el.style.minHeight = '';
    el.appendChild(frag);
    w.frags.delete(el);
  }

  /** Trả nội dung tất cả card đang bị cất. */
  function restoreAll(container) {
    const w = windows.get(container);
    if (!w || !w.frags.size) return;
    for (const el of [...w.frags.keys()]) attachCard(el, w);
  }

  /** Tắt windowing cho 1 lưới (trả hết nội dung + ngừng theo dõi). */
  function disarmWindowing(container) {
    const w = windows.get(container);
    if (!w) return;
    if (w.io) {
      w.io.disconnect();
      w.io = null;
      w.root = undefined;
    }
    restoreAll(container);
    w.observed = new WeakSet();
    w.visible = new WeakSet();
  }

  // Hàng đợi cất nội dung — xử lý theo ngân sách mỗi frame để không chặn cuộn.
  let detachQueue = [];
  let rafId = null;
  let detachPaused = false;

  function pumpDetach() {
    if (rafId !== null) return;
    // Đang cuộn -> tạm hoãn, chạy lại khi người dùng dừng cuộn (tránh giật).
    if (scrolling) {
      detachPaused = true;
      return;
    }
    rafId = requestAnimationFrame(() => {
      rafId = null;
      const start = performance.now();
      let n = 0;
      while (detachQueue.length && n < 20 && performance.now() - start < 8) {
        const job = detachQueue.shift();
        try {
          detachCard(job.el, job.w);
        } catch (e) {
          /* bo qua */
        }
        n++;
      }
      if (detachQueue.length) pumpDetach();
    });
  }

  /**
   * Đồng bộ windowing cho lưới: bật/tắt theo số card, theo dõi card mới.
   */
  function syncWindowing(container, items) {
    const w = getWin(container);

    if (items.length < WINDOW_MIN_CARDS) {
      if (w.io || w.frags.size) disarmWindowing(container);
      return;
    }

    const root = windowRoot();
    if (!w.io || w.root !== root) {
      if (w.io) w.io.disconnect();
      w.root = root;
      w.observed = new WeakSet();
      w.io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Gần màn hình -> hoàn trả NGAY (ưu tiên hiển thị)
              w.visible.add(entry.target);
              try {
                attachCard(entry.target, w);
              } catch (e) {
                /* bo qua */
              }
            } else {
              // Xa màn hình -> đưa vào hàng đợi cất (có ngân sách)
              w.visible.delete(entry.target);
              detachQueue.push({ el: entry.target, w });
            }
          }
          pumpDetach();
        },
        { root, rootMargin: WINDOW_MARGIN }
      );
    }

    for (const el of items) {
      if (!w.observed.has(el)) {
        w.observed.add(el);
        w.io.observe(el);
      }
    }
  }

  /* ------------------------------------------------------------------ *
   *  Vòng lặp chính                                                    *
   * ------------------------------------------------------------------ */

  function runAll() {
    if (busy) return;
    busy = true;

    const force = rebuildAll;
    rebuildAll = false;

    try {
      document.querySelectorAll(GRID_SEL).forEach((c) => {
        try {
          // Chế độ thu gọn: KHÔNG dùng masonry, trả lưới về mặc định
          if (c.matches(ZOOM_OUT_SEL)) {
            disarmWindowing(c);
            uncolumn(c);
            c.removeAttribute(READY_ATTR);
            state.delete(c);
            return;
          }
          layout(c, force);
          syncWindowing(c, getItems(c));
        } catch (e) {
          /* bo qua tung luoi loi */
        }
      });
    } catch (e) {
      /* bo qua */
    }

    // mo lai sau khi MutationObserver da nhan cac thay doi cua chinh ta
    setTimeout(() => {
      busy = false;
    }, 0);
  }

  // Ghi nhận hoạt động cuộn (scroll không bubble -> dùng capture trên document).
  function onScrollActivity() {
    scrolling = true;
    if (scrollEndTimer) clearTimeout(scrollEndTimer);
    scrollEndTimer = setTimeout(() => {
      scrolling = false;
      if (pendingDeferred) {
        pendingDeferred = false;
        runAll();
      }
      if (detachPaused) {
        detachPaused = false;
        pumpDetach();
      }
    }, 160);
  }

  const schedule = (ms, urgent) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (scrolling && !urgent) {
        pendingDeferred = true; // chạy lại khi dừng cuộn
        return;
      }
      runAll();
    }, ms);
  };

  function boot() {
    runAll();

    // font web doi xong -> chi dung lai khi nguoi dung CHUA cuon (tranh nhay trang)
    try {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          const scroller = document.querySelector('.page-wrapper') || document.scrollingElement;
          if ((scroller ? scroller.scrollTop : 0) < 50) {
            rebuildAll = true;
            schedule(60);
          }
        });
      }
    } catch (e) {
      /* bo qua */
    }

    // doi kich thuoc man hinh -> dung lai toan bo
    let lastW = window.innerWidth;
    window.addEventListener('resize', () => {
      if (Math.abs(window.innerWidth - lastW) < 2) return;
      lastW = window.innerWidth;
      rebuildAll = true;
      schedule(200);
    });

    // theo dõi hoạt động cuộn để hoãn việc nặng (windowing, dựng lại) lúc đang cuộn
    document.addEventListener('scroll', onScrollActivity, { passive: true, capture: true });

    // theme them san pham (infinite scroll) / render lai / doi kieu hien thi.
    // Chỉ phản ứng với thay đổi ở CẤP LƯỚI/CỘT -> bỏ qua việc windowing cất/trả
    // nội dung bên trong card (tránh vòng lặp) và các thay đổi không liên quan
    // (drawer, header, animation...).
    try {
      const mo = new MutationObserver((mutations) => {
        if (busy) return;
        let needsRun = false;
        let urgent = false;
        for (const m of mutations) {
          if (m.type === 'attributes') {
            // đổi kiểu hiển thị lưới (vd: 1 cột / zoom-out) -> phải dựng lại số cột
            rebuildAll = true;
            needsRun = true;
            continue;
          }
          const nodes = [...m.addedNodes, ...m.removedNodes];
          // (1) node được thêm/bớt CHÍNH LÀ lưới hoặc cột -> gấp (đổi cấu trúc)
          if (nodes.some((n) => n.nodeType === 1 && (isCol(n) || (n.matches && n.matches(GRID_SEL))))) {
            needsRun = true;
            urgent = true;
            break;
          }
          // (2) thay đổi ngay trong lưới/cột (thêm/bớt card, vd tải thêm SP) -> gấp
          const t = m.target;
          if (
            t &&
            t.nodeType === 1 &&
            (isCol(t) || (t.matches && t.matches(GRID_SEL))) &&
            nodes.some(isItem)
          ) {
            needsRun = true;
            urgent = true;
            break;
          }
          // (3) còn lại (nội dung trong card, vùng khác) -> bỏ qua
        }
        if (needsRun) schedule(0, urgent);
      });
      mo.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['product-grid-view'],
      });
    } catch (e) {
      /* bo qua */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
